/**
 * SupplyChainOverviewPanel — SCM4 / SCM5.
 *
 * KPIs (total BoM CAPEX, lead-time critical-path summary) plus the
 * critical-path snapshot rendered via the dedicated sub-component.
 * User-authored BoM only — no intent-seeded template rows.
 */
import React, { useMemo } from 'react';
import { Activity, AlertTriangle, BarChart3, Clock } from 'lucide-react';
import type { SupplyChainPlan } from '../../../types/supplyChain';
import type { SiteEntity } from '../../../types/site';
import { computeCriticalPathResolved, projectPhasesForCriticalPath } from '../../../lib/criticalPathAnalysis';
import { CriticalPathSection } from './CriticalPathSection';
import {
  BOM_STATUS_ORDER,
  STATUS_LABELS,
  summariseStatusCounts,
} from '../../../lib/bomItemStatusFlow';
import { deriveProcurementAlerts } from '../../../lib/procurementAlerts';
import { buildCapexVarianceTable, isCapexProvenanceBomDerived } from '../../../lib/itemizedCapex';
import { CapexVarianceTable } from './CapexVarianceTable';
import { ScmDataDisclaimer } from './ScmDataDisclaimer';
import { computePerPhaseCapexFromBom } from '../../../lib/syncCapexFromSupplyChain';
import { sortFinancialPhases } from '../../../types/financialAssumptions';

export interface SupplyChainOverviewPanelProps {
  plan: SupplyChainPlan | undefined;
  inServiceDate: string | null;
  site?: SiteEntity;
  onSyncCapexToFinancialModel?: () => void | Promise<void>;
}

/** Format a USD number in the most compact form that still reads natural. */
function formatUsd(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}k`;
  return `$${value.toFixed(0)}`;
}

export const SupplyChainOverviewPanel: React.FC<SupplyChainOverviewPanelProps> = ({
  plan,
  inServiceDate,
  site,
  onSyncCapexToFinancialModel,
}) => {
  const totals = useMemo(() => {
    const bom = plan?.bom ?? [];
    let priced = 0;
    let unpriced = 0;
    let total = 0;
    for (const item of bom) {
      if (item.totalCostUsd == null) {
        unpriced++;
        continue;
      }
      priced++;
      total += item.totalCostUsd;
    }
    return { itemCount: bom.length, priced, unpriced, total };
  }, [plan?.bom]);

  // D1 — when the owning project is phased, score each phase's BoM lines
  // against ITS OWN COD (not the single project COD), so a later-phase line is
  // not falsely flagged overdue against phase-1's date. Phases come off the
  // live site intent; single-phase / untagged BoMs fall back to the single-COD
  // path (byte-identical to the prior behaviour).
  const phases = useMemo(
    () => projectPhasesForCriticalPath(site?.projectIntent),
    [site?.projectIntent],
  );
  const criticalPath = useMemo(
    () => computeCriticalPathResolved(plan, inServiceDate, phases),
    [plan, inServiceDate, phases],
  );

  // D5 — true when the financial model's capexTotalUsd was itself synced FROM
  // this BoM (so a BoM-vs-model variance is a self-comparison, not a cross-check).
  const capexIsBomDerived = useMemo(
    () => isCapexProvenanceBomDerived(site?.financialAssumptions?.fieldProvenance?.capexTotalUsd),
    [site?.financialAssumptions?.fieldProvenance],
  );

  // Per-phase CAPEX rollup — grounded purely on priced BoM lines bucketed to
  // the site's financial phases. Only shown when the project is genuinely
  // phased; never fabricated.
  const phaseRollup = useMemo(() => {
    const finPhases = site?.financialAssumptions?.phases;
    if (!finPhases || finPhases.length < 2) return null;
    const sorted = sortFinancialPhases([...finPhases]);
    const rollups = computePerPhaseCapexFromBom(plan, sorted);
    const cells = sorted.map(p => {
      const r = rollups.find(x => x.phaseId === p.phaseId);
      return {
        phaseId: p.phaseId,
        label: p.label ?? `Phase ${p.sequence}`,
        capacityMW: p.capacityMW,
        capexTotalUsd: r?.capexTotalUsd ?? null,
        lineCount: (r?.pricedItemCount ?? 0) + (r?.unpricedItemCount ?? 0),
        codDate: p.inServiceDate,
      };
    });
    const grandTotal = cells.reduce((s, c) => s + (c.capexTotalUsd ?? 0), 0);
    if (grandTotal <= 0) return null;
    return { cells, grandTotal };
  }, [site?.financialAssumptions?.phases, plan]);

  const PHASE_SWATCHES = ['var(--primary)', 'var(--chart-3)', 'var(--chart-6)', 'var(--chart-5)', 'var(--chart-4)'];

  const bomIsEmpty = !plan || plan.bom.length === 0;

  if (bomIsEmpty) {
    return (
      <div data-testid="scm-overview-empty">
        <div className="da-empty">
          <span className="da-empty__icon"><Activity className="w-5 h-5" aria-hidden /></span>
          <div className="da-empty__title">No CAPEX yet</div>
          <p className="da-empty__body">
            No BoM line items yet. Upload a BoM PDF/Excel, add items manually on the
            <strong> Equipment (BOM)</strong> tab, or import vendor quotes — quotes-only plans
            appear here once linked.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="scm-overview-layout-grid">
      <div className="xl:col-span-12">
        <ScmDataDisclaimer compact />
      </div>
      <div className="xl:col-span-12 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="scm-overview-kpis">
        <div className="da-panel scm-ov-kpi">
          <div className="da-panel__body">
            <span className="da-kpi__k">
              <BarChart3 className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden />
              BoM-priced CAPEX
            </span>
            <span className="da-kpi__v">
              <span className="da-kpi__num da-kpi__num--accent">{formatUsd(totals.total)}</span>
            </span>
            <div className="type-body text-text-secondary mt-1">
              {totals.priced} priced · {totals.unpriced} unpriced · {totals.itemCount} total items
            </div>
          </div>
        </div>

        <div className="da-panel scm-ov-kpi">
          <div className="da-panel__body">
            <span className="da-kpi__k">
              <Clock className="inline-block w-3 h-3 mr-1 align-[-1px]" aria-hidden />
              Critical path
            </span>
            <span className="da-kpi__v">
              <span className="da-kpi__num">{criticalPath.criticalPathItems.length}</span>
              <span className="da-kpi__unit">items</span>
            </span>
            <div className="type-body text-text-secondary mt-1">
              {criticalPath.userFlaggedItems.length > 0 && (
                <span>{criticalPath.userFlaggedItems.length} designated</span>
              )}
              {criticalPath.userFlaggedItems.length > 0 && criticalPath.scheduleAtRiskItems.length > 0 && ' · '}
              {criticalPath.scheduleAtRiskItems.length > 0 && (
                <span>{criticalPath.scheduleAtRiskItems.length} schedule at risk</span>
              )}
              {criticalPath.userFlaggedItems.length === 0 && criticalPath.scheduleAtRiskItems.length === 0 && inServiceDate && (
                <span>vs COD {new Date(inServiceDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}</span>
              )}
              {!inServiceDate && criticalPath.userFlaggedItems.length === 0 && (
                <span>No COD set — enter in-service date to activate scheduling.</span>
              )}
            </div>
            {inServiceDate && criticalPath.hasOverdueItems && (
              <div className="type-body text-error mt-1 inline-flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {criticalPath.totalNegativeSlackMonths.toFixed(0)} mo overdue
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Per-phase CAPEX rollup strip — grounded on priced BoM lines. */}
      {phaseRollup && (
        <div className="xl:col-span-12" data-testid="scm-overview-phase-rollup">
          <div className="da-panel">
            <div className="da-panel__head">
              <span className="da-type-eyebrow">Per-phase CAPEX rollup</span>
              {onSyncCapexToFinancialModel && (
                <button
                  type="button"
                  className="da-panel__action"
                  onClick={() => { void onSyncCapexToFinancialModel(); }}
                  data-testid="scm-overview-phase-rollup-sync"
                >
                  Sync to financial model →
                </button>
              )}
            </div>
            <div className="da-panel__body">
              <div className="eq-rollup">
                {phaseRollup.cells.map((c, i) => (
                  <div className="eq-rollup__cell" key={c.phaseId}>
                    <span className="eq-rollup__k">
                      <i className="eq-rollup__swatch" style={{ background: PHASE_SWATCHES[i % PHASE_SWATCHES.length] }} />
                      {c.label}{c.capacityMW != null ? ` · ${c.capacityMW} MW` : ''}
                    </span>
                    <span className="eq-rollup__v">{c.capexTotalUsd != null ? formatUsd(c.capexTotalUsd) : '—'}</span>
                    <span className="eq-rollup__sub">
                      {c.codDate ? `COD ${new Date(c.codDate).toLocaleDateString(undefined, { timeZone: 'UTC' })} · ` : ''}{c.lineCount} {c.lineCount === 1 ? 'line' : 'lines'}
                    </span>
                  </div>
                ))}
                <div className="eq-rollup__cell eq-rollup__cell--total">
                  <span className="eq-rollup__k">Total programme CAPEX</span>
                  <span className="eq-rollup__v">{formatUsd(phaseRollup.grandTotal)}</span>
                  <span className="eq-rollup__sub">{totals.itemCount} lines</span>
                </div>
              </div>
              <div className="eq-phasebar" role="img" aria-label="Phase CAPEX composition">
                {phaseRollup.cells.map((c, i) => {
                  const pct = phaseRollup.grandTotal > 0 ? ((c.capexTotalUsd ?? 0) / phaseRollup.grandTotal) * 100 : 0;
                  return <span key={c.phaseId} style={{ width: `${pct}%`, background: PHASE_SWATCHES[i % PHASE_SWATCHES.length] }} />;
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="xl:col-span-12 min-w-0 w-full border border-border bg-background p-4" data-testid="scm-overview-capex-variance">
        <CapexVarianceTable
          data={buildCapexVarianceTable(
            plan,
            // D5 — suppress the self-comparison: when the model's capexTotalUsd was
            // ITSELF derived from this BoM (sync stamps provenance source 'derived'
            // + cellReference 'supplyChainPlan.bom'), the "Model" column is the SAME
            // number as the BoM-derived total and the Δ is a meaningless ~0% that
            // dresses up as an independent cross-check. Pass null so the table drops
            // the Model/Δ columns (BoM-only view) instead of asserting a false 0%.
            capexIsBomDerived ? null : (site?.financialAssumptions?.capexTotalUsd ?? null),
          )}
          caption={capexIsBomDerived ? 'BoM-derived CAPEX (model is BoM-synced — no independent cross-check)' : 'CAPEX variance (risk cue)'}
        />
        {onSyncCapexToFinancialModel && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => { void onSyncCapexToFinancialModel(); }}
              className="px-3 py-1.5 type-body uppercase tracking-widest rounded-md border border-primary/40 text-primary hover:bg-primary/10"
              data-testid="scm-sync-capex-to-fa"
            >
              Apply BoM CAPEX to financial model
            </button>
            <span className="type-body text-text-muted">
              Updates financial assumptions from priced BoM lines, including procurement deposit/terms summary (explicit sync — not automatic).
            </span>
          </div>
        )}
      </div>

      <div className="xl:col-span-12 space-y-4">
        <StatusCountsStrip plan={plan} />

        <ProcurementAlertsStrip plan={plan} inServiceDate={inServiceDate} />

        {criticalPath.criticalPathItems.length > 0 && (
          <CriticalPathSection result={criticalPath} />
        )}
      </div>

      <style>{OVERVIEW_CAPEX_CSS}</style>
    </div>
  );
};

/**
 * Surface-local Aurora Cockpit chrome for the CAPEX overview (round-2
 * `round2-equipment.html`): the KPI cells + the per-phase CAPEX rollup strip
 * and composition bar. Layers ON TOP of the shared `.da-*` tokens.
 */
const OVERVIEW_CAPEX_CSS = `
.scm-ov-kpi .da-panel__body { padding: var(--panel-pad); }
.eq-rollup { display: grid; grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr)); gap: 1px; background: var(--border-subtle); border: 1px solid var(--border); border-radius: var(--radius); overflow: hidden; }
.eq-rollup__cell { background: var(--surface-1); padding: .8125rem var(--panel-pad); }
.eq-rollup__k { display: flex; align-items: center; gap: .4375rem; font-family: var(--font-mono); font-size: .5625rem; letter-spacing: .12em; text-transform: uppercase; color: var(--text-muted); margin-bottom: .4375rem; }
.eq-rollup__swatch { width: .5rem; height: .5rem; border-radius: 2px; flex-shrink: 0; }
.eq-rollup__v { display: block; font-family: var(--font-mono); font-variant-numeric: tabular-nums; font-size: 1.125rem; font-weight: 600; letter-spacing: -.01em; color: var(--text-primary); }
.eq-rollup__sub { display: block; font-family: var(--font-mono); font-size: .5625rem; letter-spacing: .04em; color: var(--text-muted); margin-top: .1875rem; }
.eq-rollup__cell--total .eq-rollup__v { color: var(--primary); }
.eq-phasebar { display: flex; height: .4375rem; border-radius: 999px; overflow: hidden; margin-top: .75rem; border: 1px solid var(--border); }
.eq-phasebar span { display: block; height: 100%; }
`;

// ─────────────────────────────────────────────────────────────────────────────
// SCM15 — status counts strip.
// ─────────────────────────────────────────────────────────────────────────────

const ProcurementAlertsStrip: React.FC<{
  plan: SupplyChainPlan;
  inServiceDate: string | null;
}> = ({ plan, inServiceDate }) => {
  const alerts = useMemo(
    () => deriveProcurementAlerts({ plan, inServiceDate }),
    [plan, inServiceDate],
  );
  if (alerts.length === 0) return null;
  return (
    <div
      className="border border-warning/40 bg-warning/5 p-3 space-y-2"
      data-testid="scm-overview-alerts"
    >
      <div className="flex items-center gap-2 type-section text-warning">
        <AlertTriangle className="w-3 h-3" />
        Procurement alerts ({alerts.length})
      </div>
      <ul className="space-y-1.5">
        {alerts.slice(0, 5).map((a) => (
          <li
            key={a.id}
            className="type-body text-text-primary"
            data-testid={`scm-overview-alert-${a.bomItemId}`}
          >
            <span className="font-bold">{a.bomItemName}</span>
            {' — '}
            <span className="text-text-secondary">{a.message}</span>
          </li>
        ))}
      </ul>
      {alerts.length > 5 && (
        <div className="type-body text-text-secondary">
          + {alerts.length - 5} more — open the AdminPanel inbox to triage.
        </div>
      )}
    </div>
  );
};

const StatusCountsStrip: React.FC<{ plan: SupplyChainPlan }> = ({ plan }) => {
  const counts = useMemo(() => summariseStatusCounts(plan.bom), [plan.bom]);
  const nonZero = BOM_STATUS_ORDER.filter((s) => counts[s] > 0);
  if (nonZero.length === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="scm-overview-status-strip"
      role="group"
      aria-label="BoM status counts"
    >
      <span className="type-body uppercase tracking-widest text-text-muted">
        Order status:
      </span>
      {nonZero.map((s) => (
        <span
          key={s}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 type-body uppercase tracking-widest border border-border bg-background"
          data-testid={`scm-overview-status-chip-${s}`}
        >
          {STATUS_LABELS[s]}
          <span className="text-text-muted tabular-nums">{counts[s]}</span>
        </span>
      ))}
    </div>
  );
};
